import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';

// Attribution remains available when an archived conversation has no active owner.
export default function WhatsAppAttentionContext({ conversationId, lastAgent }) {
    const { authFetch } = useAuth();
    const [context, setContext] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const revision = useRef(0);
    const cancelling = useRef(false);
    const invalidate = useCallback(() => { ++revision.current; }, []);
    const load = useCallback(async () => {
        if (!conversationId || cancelling.current) return;
        const current = ++revision.current;
        try {
            const response = await authFetch(`/api/whatsapp/conversation/${conversationId}/attention-context`);
            const body = await response.json();
            if (current !== revision.current) return;
            if (!response.ok) throw new Error(body.error || 'No se pudo consultar la petición de factura');
            setContext(body);
            setError('');
        } catch (e) {
            if (current === revision.current) setError(e.message);
        }
    }, [authFetch, conversationId]);

    useEffect(() => {
        setContext(null);
        setError('');
        setNotice('');
        setBusy(false);
        load();
        const timer = setInterval(load, 60000);
        window.addEventListener('wa-lifecycle-updated', load);
        return () => {
            invalidate();
            clearInterval(timer);
            window.removeEventListener('wa-lifecycle-updated', load);
        };
    }, [load, invalidate]);

    const resolveRequest = async (action) => {
        const requestId = context?.pending_file_request?.id;
        if (!requestId || cancelling.current) return;
        cancelling.current = true;
        setBusy(true);
        setNotice('');
        const current = ++revision.current;
        try {
            const response = await authFetch(`/api/whatsapp/conversation/${conversationId}/file-requests/${requestId}/${action}`, { method: 'POST' });
            const body = await response.json();
            if (current !== revision.current) return;
            if (!response.ok) throw new Error(body.error || 'No se pudo cerrar la petición de factura');
            setContext(previous => ({ ...previous, pending_file_request: null }));
            setError('');
            setNotice(action === 'complete'
                ? 'Recogida finalizada. Los archivos se conservan y puedes solicitar otra factura.'
                : 'Petición cancelada. Los archivos recibidos se conservan.');
        } catch (e) {
            if (current === revision.current) setError(e.message);
        } finally {
            cancelling.current = false;
            if (current === revision.current) setBusy(false);
        }
    };

    const receivedCount = context?.pending_file_request?.attachment_count || 0;

    return <div className="px-5 py-2 text-xs text-slate-300 border-b border-slate-800 space-y-2">
        {lastAgent && <p>Último agente: {lastAgent}</p>}
        {context?.pending_file_request && <div className="flex flex-wrap items-center justify-between gap-3">
            <span>{receivedCount > 0
                ? `${receivedCount} ${receivedCount === 1 ? 'archivo recibido' : 'archivos recibidos'} · Pendiente de finalizar recogida`
                : 'Petición de factura pendiente'}</span>
            <div className="flex flex-wrap gap-2">
                {receivedCount > 0 && <button type="button" onClick={() => resolveRequest('complete')} disabled={busy}
                    className="px-2 py-1 rounded border border-amber-500/40 text-amber-300 disabled:opacity-50">
                    Finalizar recogida
                </button>}
                <button type="button" onClick={() => resolveRequest('cancel')} disabled={busy}
                    className="px-2 py-1 rounded border border-amber-500/40 text-amber-300 disabled:opacity-50">
                    {busy ? 'Procesando…' : 'Cancelar petición de factura'}
                </button>
            </div>
        </div>}
        {notice && <p role="status">{notice}</p>}
        {context?.assignments?.length > 0 && <details>
            <summary className="cursor-pointer">Historial de agentes</summary>
            <ul className="mt-1 space-y-1">
                {context.assignments.map((assignment, index) => <li key={`${assignment.started_at}-${index}`}>
                    {assignment.agent_id} · {new Date(assignment.started_at).toLocaleString('es-ES')}
                    {assignment.ended_at ? ` — ${new Date(assignment.ended_at).toLocaleString('es-ES')}` : ' · Asignación actual'}
                </li>)}
            </ul>
        </details>}
        {error && <p role="alert" className="text-amber-300">{error} <button type="button" onClick={load}>Reintentar</button></p>}
    </div>;
}
